import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { Message, ScopePack, AIService } from '../shared/models/chat.model';
import { SYSTEM_PROMPT } from '../constant/scope.prompt';
import { environment } from 'src/environments/environment';
import { ScopeFunnelTracker } from '../shared/analytics/scope-funnel.tracker';

interface ChatSessionData {
  messages: Message[];
  conversationHistory: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
  scopePack: ScopePack | null;
  timestamp: number;
  customerId?: string;
  hasCompleted: boolean;
  customerCompany?: string; // Add this field
  /** Project being edited, if any. Persisted because the save dialog picks
   *  the PUT-vs-POST branch off it — losing it across a reload would file
   *  the user's edits as a brand-new project. */
  editingProjectId?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  // State management
  private messagesSubject = new BehaviorSubject<Message[]>([]);
  private scopePackSubject = new BehaviorSubject<ScopePack | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private selectedAIServiceSubject = new BehaviorSubject<AIService>('gemini');
  private errorSubject = new BehaviorSubject<string | null>(null);
  private currentCustomerIdSubject = new BehaviorSubject<string | null>(null);
  private conversationCompleteSubject = new BehaviorSubject<boolean>(false);

  private readonly GEMINI_PROXY_URL = `${environment.apiUrl}ai/gemini/generate`;

  private conversationHistory: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [];
  private hasConversationCompleted: boolean = false;

  // ── Edit-project-scope mode ──────────────────────────────────────────
  // When the customer clicks "Edit Scope" on the project detail page they
  // land back on the create-brief chat, but the flow behaves differently:
  //   - the existing project's scope is loaded into scopePackSubject
  //   - the LLM sees an EDIT-MODE system message describing what already
  //     exists and how to make targeted patches
  //   - Save & Approve does a PUT projects/{id} instead of POST projects,
  //     preserving invited vendors / TOE state / bids
  // Reading `editingProjectId` from outside the service is how the save
  // dialog knows which branch to take. Untouched for the new-brief flow.
  private editingProjectId: string | null = null;
  private editingProjectSnapshot: any = null;

  get isEditingProject(): boolean { return this.editingProjectId !== null; }
  getEditingProjectId(): string | null { return this.editingProjectId; }
  getEditingProjectSnapshot(): any { return this.editingProjectSnapshot; }

  messages$ = this.messagesSubject.asObservable();
  scopePack$ = this.scopePackSubject.asObservable();
  isLoading$ = this.isLoadingSubject.asObservable();
  selectedAIService$ = this.selectedAIServiceSubject.asObservable();
  error$ = this.errorSubject.asObservable();
  currentCustomerId$ = this.currentCustomerIdSubject.asObservable();
  conversationComplete$ = this.conversationCompleteSubject.asObservable();

  private readonly CHAT_STORAGE_KEY = 'studioip_chat_session';
  private readonly SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000;
  
  constructor(
    private http: HttpClient,
    private scopeFunnel: ScopeFunnelTracker,
  ) {
    this.initializeChat();
    this.setupAutoSave();

    // A scope abandoned by closing the tab is the one case that cannot be
    // reconstructed afterwards — the in-progress state dies with the page.
    // This is the event behind "N clients abandoned scope", so it has to be
    // captured at the last possible moment.
    //
    // `pagehide` rather than `beforeunload`: Safari and mobile browsers do
    // not reliably fire beforeunload, and the analytics SDKs use sendBeacon,
    // which survives pagehide. Registered once, in the service, because the
    // ten components that drive scoping would otherwise each need their own.
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => {
        if (this.scopeFunnel.isInProgress) {
          this.scopeFunnel.scopeAbandoned('page_closed');
        }
      });
    }
  }

  initializeChat(loadFromStorage: boolean = true): void {
    if (loadFromStorage) {
      const savedChat = this.loadChatFromStorage();
      if (savedChat) {
        this.conversationHistory = savedChat.conversationHistory;
        this.messagesSubject.next(savedChat.messages);
        this.hasConversationCompleted = savedChat.hasCompleted || false;
        this.conversationCompleteSubject.next(this.hasConversationCompleted);
        // Restore the edit target before anything else can read it: the save
        // dialog branches PUT-vs-POST on this, and enterEditMode uses it to
        // tell a genuine resume from a fresh entry.
        this.editingProjectId = savedChat.editingProjectId ?? null;

        if (savedChat.scopePack) {
          this.scopePackSubject.next(savedChat.scopePack);
        }

        if (savedChat.customerId) {
          this.currentCustomerIdSubject.next(savedChat.customerId);
        }

        // console.log('Chat loaded from localStorage. Has completed:', this.hasConversationCompleted);

        if (this.hasConversationCompleted) {
          this.prepareForEditing();
        }

        return;
      }
    }

    this.resetToFreshChat();
  }

  private prepareForEditing(): void {
    console.log('Preparing conversation for editing...');

    const lastMessage = this.messagesSubject.getValue().slice(-1)[0];
    if (lastMessage && lastMessage.content.includes('Chat with me if you want to edit something')) {
      const newMessages = this.messagesSubject.getValue().slice(0, -1);
      const editingPrompt: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: "What would you like to change? You can modify the budget, timeline, scope, or any other details.",
        timestamp: Date.now()
      };
      this.messagesSubject.next([...newMessages, editingPrompt]);

      const historyWithoutCompletion = this.conversationHistory.slice(0, -1);
      this.conversationHistory = [
        ...historyWithoutCompletion,
        { role: 'assistant', content: editingPrompt.content }
      ];

      this.hasConversationCompleted = false;
      this.conversationCompleteSubject.next(false);
    }
  }

  private resetToFreshChat(): void {
    this.conversationHistory = [
      {
        role: 'system',
        content: SYSTEM_PROMPT
      }
    ];

    this.scopePackSubject.next(null);
    this.errorSubject.next(null);
    this.hasConversationCompleted = false;
    this.conversationCompleteSubject.next(false);

    const welcomeMessage: Message = {
      id: 'welcome',
      role: 'assistant',
      content: `Hello. \n\nWhat type of service are you looking for (e.g., film, photography, design, animation, audio)? Please describe your project brief so I can define the Scope of Work and Vendor Requirements for you.`,
      timestamp: Date.now()
    };

    this.messagesSubject.next([welcomeMessage]);
    this.saveChatToStorage();
  }

  private loadCustomerSession(customerId: string): void {
    try {
      const allSessions = localStorage.getItem('studioip_all_sessions');
      if (allSessions) {
        const sessions = JSON.parse(allSessions);
        const customerSession = sessions[customerId];

        if (customerSession && customerSession.timestamp) {
          const isExpired = Date.now() - customerSession.timestamp > this.SESSION_EXPIRY_MS;

          if (!isExpired) {
            this.conversationHistory = customerSession.conversationHistory || [];
            this.messagesSubject.next(customerSession.messages || []);
            this.scopePackSubject.next(customerSession.scopePack || null);
            this.hasConversationCompleted = customerSession.hasCompleted || false;
            this.conversationCompleteSubject.next(this.hasConversationCompleted);

            if (this.hasConversationCompleted) {
              this.prepareForEditing();
            }

            console.log(`Loaded session for customer: ${customerId}`);
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error loading customer session:', error);
    }

    this.initializeChat(false);
  }

  // ==================== ONLY FOR DISPLAY - REMOVE INTERMEDIATE JSONS FROM UI ====================
  
  /**
   * This function ONLY cleans the text for DISPLAY in the chat UI
   * It does NOT affect scope pack extraction which uses the raw response
   */
  private cleanForDisplay(text: string): string {
    if (!text) return text;
    
    let cleaned = text;
    
    // Remove markdown code blocks that contain JSON
    cleaned = cleaned.replace(/```json\s*\{[\s\S]*?\}\s*```/g, '');
    cleaned = cleaned.replace(/```\s*\{[\s\S]*?\}\s*```/g, '');
    
    // Remove labels like "**Internal Generation (for SSE):**" with markdown code blocks
    cleaned = cleaned.replace(/\*\*Internal Generation[^*]*\*\*:\s*```json\s*\{[\s\S]*?\}\s*```/g, '');
    cleaned = cleaned.replace(/\*\*Internal Generation[^*]*\*\*:\s*```\s*\{[\s\S]*?\}\s*```/g, '');
    cleaned = cleaned.replace(/Internal Generation[^:]*:\s*```json\s*\{[\s\S]*?\}\s*```/g, '');
    cleaned = cleaned.replace(/Internal Generation[^:]*:\s*```\s*\{[\s\S]*?\}\s*```/g, '');
    
    // Remove labels with JSON without markdown
    cleaned = cleaned.replace(/\*\*Internal Generation[^*]*\*\*:\s*\{[\s\S]*?\}/g, '');
    cleaned = cleaned.replace(/Internal Generation[^:]*:\s*\{[\s\S]*?\}/g, '');
    
    // Remove service_mapping JSON
    cleaned = cleaned.replace(/\{\s*"mapped_service"\s*:[\s\S]*?"status"\s*:\s*"[^"]+"\s*\}/g, '');
    cleaned = cleaned.replace(/service_mapping\s*=\s*\{[\s\S]*?\}/g, '');
    
    // Remove locked_scope JSON with markdown
    cleaned = cleaned.replace(/```json\s*\{\s*"locked_scope"\s*:\s*\{[\s\S]*?\}\s*\}\s*```/g, '');
    cleaned = cleaned.replace(/```\s*\{\s*"locked_scope"\s*:\s*\{[\s\S]*?\}\s*\}\s*```/g, '');
    
    // Remove locked_scope JSON without markdown
    cleaned = cleaned.replace(/\{\s*"locked_scope"\s*:\s*\{[\s\S]*?\}\s*\}/g, '');
    cleaned = cleaned.replace(/locked_scope\s*=\s*\{[\s\S]*?\}/g, '');
    
    // Remove any remaining JSON code blocks
    cleaned = cleaned.replace(/```json\s*\{[\s\S]*?\}\s*```/g, '');
    cleaned = cleaned.replace(/```\s*\{[\s\S]*?\}\s*```/g, '');
    
    // Remove lines that are just "---"
    cleaned = cleaned.replace(/^---\s*$/gm, '');
    
    // Remove standalone JSON that looks like intermediate data
    cleaned = cleaned.replace(/\n?\s*\{\s*"deliverables"\s*:\s*\[[^\]]*\][^}]*\}\s*\n?/g, '\n');
    
    // Clean up extra blank lines
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    
    // Trim whitespace
    cleaned = cleaned.trim();
    
    // If after cleaning the text is empty, return a friendly message
    if (!cleaned) {
      return "I'm processing your request. Please continue.";
    }
    
    return cleaned;
  }

  // ==================== SEND MESSAGE ====================
  
  async sendMessage(userInput: string): Promise<void> {
    if (!userInput.trim() || this.isLoadingSubject.getValue()) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userInput.trim(),
      timestamp: Date.now()
    };

    const currentMessages = this.messagesSubject.getValue();
    console.log('Adding user message to UI:', userMessage);
    this.messagesSubject.next([...currentMessages, userMessage]);

    // ── Scope funnel ──────────────────────────────────────────────────────
    // Measured on the first USER TURN, not on page load: opening the brief
    // screen and leaving is not "starting a scope", and counting it as one
    // would inflate abandonment against a denominator of window-shoppers.
    // Computed before the push below, while the history still has no user turn.
    const isFirstUserTurn =
      this.conversationHistory.filter((m) => m.role === 'user').length === 0;
    if (isFirstUserTurn) {
      this.scopeFunnel.scopeStarted(this.scopeSource(), {
        editing: !!this.editingProjectId,
      });
    } else {
      this.scopeFunnel.clientTurn();
    }

    this.conversationHistory.push({ role: 'user', content: userInput.trim() });
    this.saveChatToStorage();
    this.isLoadingSubject.next(true);
    this.errorSubject.next(null);

    try {
      const responseText = await this.callGemini();
      console.log('Gemini Response (first 300 chars):', responseText.substring(0, 300) + '...');
      
      // ✅ STEP 1: Extract scope pack from RAW response (this must work as before)
      const scopePack = this.extractScopePack(responseText);

      if (scopePack) {
        // We got the final JSON - update scope pack
        console.log('✅ Scope pack extracted:', scopePack);
        this.scopePackSubject.next(scopePack);

        // The funnel converts here — this is the single moment a scope goes
        // from "in progress" to "done", across every entry point.
        this.scopeFunnel.scopeGenerated(scopePack);

        const completionText = 'I have analyzed your project and created a detailed project overview. You can now view the complete overview in the summary panel on the left. Chat with me if you want to edit something.';

        const completionMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: Date.now()
        };

        this.messagesSubject.next([...this.messagesSubject.getValue(), completionMessage]);

        const words = completionText.split(' ');
        let index = 0;

        const interval = setInterval(() => {
          if (index >= words.length) {
            clearInterval(interval);
            completionMessage.content = completionText;
            this.conversationHistory.push({
              role: 'assistant',
              content: 'I have completed the scope analysis and generated the scope pack.'
            });
            this.hasConversationCompleted = true;
            this.conversationCompleteSubject.next(true);
            this.saveChatToStorage();
            return;
          }

          completionMessage.content += (index === 0 ? '' : ' ') + words[index];
          this.messagesSubject.next([...this.messagesSubject.getValue()]);
          index++;
        }, 30);

      } else {
        // ✅ STEP 2: For regular conversation, clean the response for DISPLAY only
        const displayText = this.cleanForDisplay(responseText);
        
        // If cleaned text is empty, don't show anything
        if (!displayText || displayText.trim() === '') {
          console.log('Response was empty after cleaning, skipping display');
          this.isLoadingSubject.next(false);
          return;
        }
        
        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: Date.now()
        };

        this.messagesSubject.next([...this.messagesSubject.getValue(), assistantMessage]);

        const words = displayText.split(' ');
        let index = 0;

        const interval = setInterval(() => {
          if (index >= words.length) {
            clearInterval(interval);
            assistantMessage.content = displayText;
            // Store the ORIGINAL response in history (not cleaned)
            // This ensures the AI has full context for future messages
            this.conversationHistory.push({
              role: 'assistant',
              content: responseText  // ✅ Store raw response, not cleaned
            });

            // A plain reply (a question or an acknowledgement) normally means
            // the brief is back in progress, so the review panel closes.
            // NOT in edit mode: the project is already complete and saved,
            // so one clarifying question must not withdraw Save & Approve —
            // that is what forced the user back through the whole interview
            // to reach approval again (QA 07 Sept BUG-10).
            if (this.hasConversationCompleted && !this.isEditingProject) {
              this.hasConversationCompleted = false;
              this.conversationCompleteSubject.next(false);
            }

            this.saveChatToStorage();
            return;
          }

          assistantMessage.content += (index === 0 ? '' : ' ') + words[index];
          this.messagesSubject.next([...this.messagesSubject.getValue()]);
          index++;
        }, 30);
      }

    } catch (error: any) {
      console.error('Full Error Details in sendMessage:', error);
      console.error('Error Status:', error.status);
      console.error('Error Message:', error.message);
      console.error('Error Response:', error.error);

      let errorMsg = "I encountered an error processing your request. Please try again.";

      if (error.status === 401 || error.message?.includes('401') || error.message?.includes('API key')) {
        errorMsg = `**Authentication Error**: The Gemini API key is invalid. Please check your API key.`;
      } else if (error.status === 429) {
        errorMsg = "**Rate Limit Exceeded**: Please wait a moment before trying again.";
      } else if (error.message?.includes('Network Error')) {
        errorMsg = "**Network Error**: Please check your internet connection.";
      } else if (error.error?.error?.message) {
        errorMsg = `**Wait**: ${error.error.error.message}`;
      }

      this.errorSubject.next(errorMsg);

      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: errorMsg,
        timestamp: Date.now()
      };

      this.messagesSubject.next([...this.messagesSubject.getValue(), errorMessage]);
      this.conversationHistory.push({ role: 'assistant', content: errorMsg });
      this.saveChatToStorage();

    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  private async callGemini(): Promise<string> {
    const url = this.GEMINI_PROXY_URL;

    // Concatenate ALL system messages so edit-mode's addendum layers on
    // top of the base SYSTEM_PROMPT. The new-brief flow only has one
    // system message and this join is a no-op for it.
    const systemMessages = this.conversationHistory
      .filter(msg => msg.role === 'system' && msg.content && msg.content.trim().length > 0)
      .map(msg => msg.content);

    const contents = this.conversationHistory
      .filter(msg => msg.role !== 'system' && msg.content && msg.content.trim().length > 0)
      .map(msg => ({
        role: msg.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: msg.content }]
      }));

    const body: any = {
      contents: contents,
      generationConfig: {
        temperature: 0.7,
        topK: 1,
        topP: 1,
        maxOutputTokens: 65536
      }
    };

    if (systemMessages.length) {
      body.systemInstruction = {
        parts: [{ text: systemMessages.join('\n\n') }]
      };
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    try {
      const response: any = await this.http.post(url, body, { headers }).toPromise();
      const text = response.candidates[0].content.parts[0].text || '';
      return text;
    } catch (error) {
      console.error('Gemini API Error:', error);
      throw error;
    }
  }

  // ==================== EXTRACT SCOPE PACK - UNCHANGED ====================
  
  private extractScopePack(text: string): ScopePack | null {
    const trimmedText = text.trim();
    console.log('Checking for scope pack in text:', trimmedText.substring(0, 200) + '...');

    const jsonMatch = trimmedText.match(/\{[\s\S]*\}/);

    if (jsonMatch) {
      try {
        let jsonString = jsonMatch[0];

        jsonString = jsonString
          .replace(/^```(?:json)?\s*/g, '')
          .replace(/\s*```$/g, '')
          .trim();

        const parsed = JSON.parse(jsonString);
        
        console.log('Parsed JSON structure:', Object.keys(parsed));

        const isNewFormat = parsed.classification &&
            parsed.classification.main_service &&
            parsed.scope_of_work &&
            Array.isArray(parsed.scope_of_work.in_scope) &&
            Array.isArray(parsed.scope_of_work.out_of_scope) &&
            parsed.budget_timeline &&
            parsed.proposed_date &&
            parsed.feasibility &&
            parsed.feasibility.client_decision !== undefined &&
            parsed.feasibility.risk_summary !== undefined &&
            parsed.hiring_summary &&
            parsed.conversation_summary;

        const isOldFormat = parsed.classification &&
            parsed.scope_of_work &&
            parsed.budget_timeline &&
            parsed.client_decision &&
            parsed.vendor_capability_summary &&
            parsed.conversation_summary;

        if (isNewFormat) {
          console.log('✅ Found valid scope pack JSON (NEW format with hiring_summary)');
          return parsed as ScopePack;
        } else if (isOldFormat) {
          console.log('✅ Found valid scope pack JSON (OLD format with vendor_capability_summary)');
          return parsed as ScopePack;
        } else {
          console.log('❌ Not a valid scope pack - missing required fields');
        }
      } catch (error) {
        console.log('Failed to parse JSON from response:', error);
      }
    }

    return null;
  }

  // ==================== STORAGE METHODS ====================
  
  saveChatToStorage(): void {
    try {
      const chatData: ChatSessionData = {
        messages: this.messagesSubject.getValue(),
        conversationHistory: this.conversationHistory,
        scopePack: this.scopePackSubject.getValue(),
        timestamp: Date.now(),
        customerId: this.currentCustomerIdSubject.getValue() || undefined,
        hasCompleted: this.hasConversationCompleted,
        editingProjectId: this.editingProjectId,
      };

      localStorage.setItem(this.CHAT_STORAGE_KEY, JSON.stringify(chatData));

      if (this.currentCustomerIdSubject.getValue()) {
        this.saveCustomerSession(chatData);
      }

      console.log('Chat saved to localStorage. Has completed:', this.hasConversationCompleted);
    } catch (error) {
      console.error('Error saving chat to localStorage:', error);
    }
  }

  private loadChatFromStorage(): ChatSessionData | null {
    try {
      const savedData = localStorage.getItem(this.CHAT_STORAGE_KEY);
      if (savedData) {
        const parsedData: ChatSessionData = JSON.parse(savedData);
        const isExpired = Date.now() - parsedData.timestamp > this.SESSION_EXPIRY_MS;
        if (!isExpired) {
          return parsedData;
        } else {
          console.log('Chat session expired, clearing localStorage');
          this.clearStorage();
        }
      }
    } catch (error) {
      console.error('Error loading chat from localStorage:', error);
      this.clearStorage();
    }
    return null;
  }

  private clearStorage(): void {
    localStorage.removeItem(this.CHAT_STORAGE_KEY);
  }

  private setupAutoSave(): void {
    window.addEventListener('beforeunload', () => {
      this.saveChatToStorage();
    });
  }

  /**
   * Which surface this scope was started from.
   *
   * Ten components drive this service and they are NOT equivalent: a scope
   * started by a client in the customer console and one an ops user typed on
   * the client's behalf in the internal console are different business
   * events, and averaging them together makes the funnel meaningless.
   *
   * Read from the URL rather than passed in by callers, so a new entry point
   * is labelled automatically instead of silently inheriting "unknown" until
   * someone remembers to thread a parameter through.
   */
  private scopeSource(): string {
    try {
      const path = (window?.location?.pathname || '').toLowerCase();
      if (path.includes('/internal-team')) return 'internal_console';
      if (path.includes('/customer')) return 'customer_console';
      if (path.includes('/vendor')) return 'vendor_console';
      if (path.includes('/brief')) return 'brief';
      if (path === '/' || path.includes('/home')) return 'home_page';
      return path || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  setAIService(service: AIService): void {
    if (service !== 'gemini') {
      service = 'gemini';
      console.warn('Only Gemini service is supported. Switching to Gemini.');
    }
    this.selectedAIServiceSubject.next(service);
    this.resetSession();
  }

  resetSession(clearStorage: boolean = true): void {
    // An in-progress scope being reset IS an abandonment — the client started
    // one and walked away from it. Recorded before the state is cleared,
    // because after resetToFreshChat() there is nothing left to report.
    // No-ops when no scope is running or when one just completed.
    this.scopeFunnel.scopeAbandoned('session_reset');

    // Any reset — auto or manual — leaves edit mode. Prevents a stale
    // editingProjectId from leaking into a fresh new-brief conversation
    // that would then accidentally PUT into someone else's project.
    this.clearEditMode();
    if (clearStorage) {
      this.clearStorage();

      const customerId = this.currentCustomerIdSubject.getValue();
      if (customerId) {
        try {
          const allSessions = localStorage.getItem('studioip_all_sessions');
          if (allSessions) {
            const sessions = JSON.parse(allSessions);
            delete sessions[customerId];
            localStorage.setItem('studioip_all_sessions', JSON.stringify(sessions));
          }
        } catch (error) {
          console.error('Error clearing customer session:', error);
        }
      }
    }

    this.resetToFreshChat();
  }

  clearChat(): void {
    this.resetSession(true);
  }

  getServiceName(): string {
    return 'Gemini';
  }

  get selectedAIService(): AIService {
    return this.selectedAIServiceSubject.getValue();
  }

  getMessages(): Message[] {
    return this.messagesSubject.value;
  }

  getScopePack(): ScopePack | null {
    return this.scopePackSubject.value;
  }

  updateScopePack(scopePack: ScopePack): void {
    // An edit AFTER the AI produced a scope. This is the signal behind the
    // "Manual Edits After AI" metric — how often the generated scope was not
    // good enough as-is — so it is only meaningful once one exists.
    if (this.scopePackSubject.value) {
      this.scopeFunnel.scopeEdited(scopePack, 'scope_pack_updated');
    }
    this.scopePackSubject.next(scopePack);
    this.saveChatToStorage();
  }

  clearScopePack(): void {
    this.scopePackSubject.next(null);
    this.hasConversationCompleted = false;
    this.conversationCompleteSubject.next(false);
    this.saveChatToStorage();
  }

  startEditingMode(): void {
    if (this.hasConversationCompleted) {
      console.log('Entering editing mode...');
      this.prepareForEditing();
    }
  }

  /** Seed the chat with an existing project's scope for the Edit Scope flow.
   *
   *  The chat starts in "review" state (hasConversationCompleted=true) so the
   *  customer sees their current brief on the right panel immediately. When
   *  they click "Chat with me if you want to edit something" (or the left
   *  panel's Edit button), prepareForEditing() turns the conversation back
   *  on — same code path the new-brief flow already uses.
   *
   *  A dedicated EDIT-MODE system message is layered on top of the base
   *  SYSTEM_PROMPT so the LLM knows to make targeted patches instead of
   *  rebuilding the scope from zero. The new-brief conversation never
   *  reaches this branch — resetSession() clears editingProjectId.
   */
  enterEditMode(projectId: string, project: any): void {
    if (!projectId || !project) return;

    // RESUME, don't restart. `?editing=<id>` stays in the URL, so a page
    // refresh re-runs loadProjectForEditing → enterEditMode. Rebuilding from
    // scratch here would discard the user's in-progress edit conversation and
    // replace their unsaved scope changes with the persisted project. Only
    // the snapshot is refreshed; history, messages and pack are left alone.
    const alreadyEditingThis =
      this.editingProjectId === projectId &&
      this.messagesSubject.getValue().length > 1;
    if (alreadyEditingThis) {
      this.editingProjectSnapshot = project;
      return;
    }

    this.editingProjectId = projectId;
    this.editingProjectSnapshot = project;

    // Reconstruct a ScopePack from the persisted project fields.
    const scopePack: any = {
      classification: project.classification || null,
      scope_of_work: project.scope_of_work || null,
      hiring_summary: project.hiring_summary
        || project.vendor_capability_summary
        || null,
      vendor_capability_summary: project.vendor_capability_summary || null,
      budget_timeline: project.budget_timeline || null,
      feasibility: project.feasibility || null,
      client_decision: project.client_decision || null,
      conversation_summary: project.conversation_summary || null,
      proposed_date: project.proposed_date || null,
    };

    const editContext = this.buildEditModeSystemMessage(project, scopePack);
    this.conversationHistory = [
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'system', content: editContext },
    ];

    // Push a warm assistant message so the vendor knows what to do next.
    // We flip hasConversationCompleted to true so the review panel opens
    // and shows the current brief immediately — the customer clicks
    // "Edit" to start chatting when they're ready.
    const projectName = project.title || project.classification?.title || 'this project';
    const welcome: Message = {
      id: `edit-welcome-${Date.now()}`,
      role: 'assistant',
      content:
        `You're editing "${projectName}". Your current brief is on the right — ` +
        `review it, then tell me what you'd like to change ` +
        `(budget, timeline, scope, deliverables, vendor requirements, anything).`,
      timestamp: Date.now(),
    };
    this.messagesSubject.next([welcome]);
    this.conversationHistory.push({ role: 'assistant', content: welcome.content });

    this.scopePackSubject.next(scopePack);
    this.errorSubject.next(null);
    this.hasConversationCompleted = true;
    this.conversationCompleteSubject.next(true);
    this.saveChatToStorage();
  }

  /** Constructs the EDIT-MODE addendum the LLM sees. Kept inline so the
   *  base SYSTEM_PROMPT stays untouched — the new-brief flow never picks
   *  up this message because it's only appended in enterEditMode(). */
  private buildEditModeSystemMessage(project: any, scopePack: any): string {
    const safe = (v: any) => {
      try { return JSON.stringify(v, null, 2); } catch { return String(v); }
    };
    return [
      '=== EDIT MODE ===',
      '',
      'The user is editing an EXISTING project, not creating a new one.',
      'A prior ScopePack has already been built for them (below). Your job',
      'is to make TARGETED patches based on what the user asks — do NOT',
      'rebuild the whole brief from scratch, do NOT ask questions that are',
      'already answered in the existing scope unless the user explicitly',
      'reopens that topic.',
      '',
      // The base SYSTEM_PROMPT runs a STAGED interview with a gate marked
      // "non-skippable — you MUST ask and get answers to ALL of" and a
      // budget question marked "NON-SKIPPABLE. You must always ask it."
      // In edit mode the conversation carries NO prior user turns — every
      // answer lives in the JSON blob below — so the model saw no evidence
      // the gate had ever been satisfied and dutifully re-asked the whole
      // set, every turn. "MUST / non-skippable" outranks "don't re-ask" in
      // any reading. Restating the gate as ALREADY ANSWERED, with the
      // values, is what actually closes it (QA 07 Sept BUG-10).
      '=== MANDATORY QUESTION GATE: ALREADY SATISFIED ===',
      '',
      'Every question in the base prompt\'s MANDATORY QUESTION GATE was',
      'answered when this project was first scoped. The answers are below.',
      'The gate is CLOSED. Do NOT re-ask any of them — not on the first',
      'turn, not before emitting the ScopePack, and not before approval.',
      'Treat each value as if the user had just told you it.',
      '',
      this.renderAnsweredGate(project, scopePack),
      '',
      'The SERVICE-SPECIFIC MANDATORY QUESTIONS are likewise already',
      'reflected in scope_of_work and hiring_summary below. Ask one only if',
      'the user\'s requested change genuinely leaves it undetermined (e.g.',
      'they switch the service entirely).',
      '',
      'Rules:',
      '  1. Read the existing ScopePack first. Every field is authoritative',
      '     unless the user asks to change it.',
      '  2. When the user requests a change ("bump budget to 3L", "add 2',
      '     more items", "shift deadline to next week"), acknowledge briefly,',
      '     update ONLY that field, and re-emit the full ScopePack with your',
      '     edit applied. Every other field stays byte-identical.',
      '  3. If the user\'s request is ambiguous, ask ONE clarifying question.',
      '     Never guess.',
      '  4. When the user is done ("that\'s all", "looks good", "save it"),',
      '     produce the final complete ScopePack in your reply so the',
      '     right-panel snapshot re-syncs, then say "Great — hit Save &',
      '     Approve when you\'re ready".',
      '  5. Preserve project identity fields the user did not touch —',
      '     title, main_service, sub_service — unless they explicitly',
      '     redirected the project.',
      '  6. This project is ALREADY APPROVABLE. The user may hit Save &',
      '     Approve at any point, including immediately. Never tell them',
      '     more information is needed before they can save, and never',
      '     withhold the ScopePack pending further answers.',
      '',
      'Existing project (id: ' + project.id + '):',
      safe({
        title: project.title,
        classification: scopePack.classification,
        scope_of_work: scopePack.scope_of_work,
        hiring_summary: scopePack.hiring_summary,
        // Previously omitted from the blob, so the model could not see them
        // and would re-ask what they already contained.
        vendor_capability_summary: scopePack.vendor_capability_summary,
        budget_timeline: scopePack.budget_timeline,
        feasibility: scopePack.feasibility,
        client_decision: scopePack.client_decision,
        conversation_summary: scopePack.conversation_summary,
        proposed_date: scopePack.proposed_date,
      }),
    ].join('\n');
  }

  /** Restates the base prompt's mandatory gate as answered, quoting the
   *  stored value for each. Anything genuinely absent is marked so the
   *  model may ask about that ONE item instead of restarting the set. */
  private renderAnsweredGate(project: any, scopePack: any): string {
    const bt = scopePack?.budget_timeline || {};
    const sow = scopePack?.scope_of_work || {};
    const cls = scopePack?.classification || {};

    const budget = bt.final_budget_value ?? bt.client_budget_value ?? null;
    const currency = bt.client_budget_currency || 'INR';
    const deadline =
      bt.client_deadline_date ||
      scopePack?.proposed_date ||
      (bt.final_timeline_days != null ? `${bt.final_timeline_days} days from start` : null);
    const refs =
      (Array.isArray(sow.dependencies) && sow.dependencies.length)
        ? sow.dependencies.join('; ')
        : null;

    const row = (label: string, value: any) =>
      `  - ${label}: ` +
      (value === null || value === undefined || value === ''
        ? '(not recorded — you MAY ask about this one item only)'
        : `ANSWERED — ${typeof value === 'string' ? value : String(value)}`);

    return [
      row('Project start / urgency', cls.urgency_flag || project.start_preference || 'as per agreed timeline'),
      row('References provided', refs),
      row('Expected deadline / delivery timeline', deadline),
      row(`Budget (${currency})`, budget),
      row('Service & sub-service', [cls.main_service, cls.sub_service].filter(Boolean).join(' / ')),
      row('Deliverables agreed',
        Array.isArray(sow.deliverables) && sow.deliverables.length
          ? `${sow.deliverables.length} item(s): ${sow.deliverables.slice(0, 6).join('; ')}`
          : null),
    ].join('\n');
  }

  /** Called by resetSession + startNewBrief to clear any edit state so the
   *  next fresh brief never accidentally PUTs into someone else's project. */
  private clearEditMode(): void {
    this.editingProjectId = null;
    this.editingProjectSnapshot = null;
  }

  isConversationComplete(): boolean {
    return this.hasConversationCompleted;
  }

  private saveCustomerInfoToSession(customer: any): void {
    const currentData = this.loadChatFromStorage();
    if (currentData && customer) {
      currentData.customerId = customer.customers_id;
      currentData.customerCompany = customer.customers_company;
      this.saveToStorage(currentData);
    }
  }

  private saveCustomerSession(chatData: ChatSessionData): void {
    const customerId = this.currentCustomerIdSubject.getValue();
    if (!customerId) return;

    try {
      let allSessions: Record<string, ChatSessionData> = {};
      const savedSessions = localStorage.getItem('studioip_all_sessions');
      if (savedSessions) {
        allSessions = JSON.parse(savedSessions);
      }

      allSessions[customerId] = chatData;
      localStorage.setItem('studioip_all_sessions', JSON.stringify(allSessions));
      console.log(`Saved session for customer: ${customerId}`);
      localStorage.setItem(this.CHAT_STORAGE_KEY, JSON.stringify(chatData));
    } catch (error) {
      console.error('Error saving customer session:', error);
    }
  }

  private saveToStorage(chatData: ChatSessionData): void {
    localStorage.setItem(this.CHAT_STORAGE_KEY, JSON.stringify(chatData));
  }

  setCurrentCustomer(customerId: string | null, customerCompany?: string): void {
    this.currentCustomerIdSubject.next(customerId);
    
    if (customerId && customerCompany) {
      const currentData = this.loadChatFromStorage();
      if (currentData) {
        currentData.customerId = customerId;
        currentData.customerCompany = customerCompany;
        this.saveToStorage(currentData);
      }
    }
    
    if (customerId) {
      this.loadCustomerSession(customerId);
    } else {
      this.resetSession(false);
    }
  }
}